import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import CustomChip from 'Components/CustomChip'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledTableHead from 'Components/StyledTableHead'
import { WithLoader } from 'Components/WithLoader'
import { AffiliateOrderLineRow } from 'Services/SalesManagement/Features/Order/Components/AffiliateOrderLineRow'
import { AffiliatePaymentMethod } from 'Services/SalesManagement/Features/Order/Components/AffiliatePaymentEventRow'
import MobileOrderRefundDialog from 'Services/SalesManagement/Features/Order/Components/MobileOrderRefundDialog'
import { RefundHistoryList } from 'Services/SalesManagement/Features/Order/Components/RefundHistoryList'
import { RefundInfo } from 'Services/SalesManagement/Features/Order/Components/RefundInfo'
import {
  useGetMobileOrderRefundListQuery,
  useGetMobileOrderRefundOptionsQuery
} from 'Services/SalesManagement/Features/Order/orderApi'
import {
  type AffiliateTicketOrderLineShape,
  fetchMobileOrder,
  fetchMobileOrderReceipt,
  requestMobileOrderLineRefund,
  requestMobileOrderRefund
} from 'Services/SalesManagement/Features/Order/orderSlice'
import useSalesManagementBreadcrumbs from 'Services/SalesManagement/Hooks/useSalesManagementBreadcrumbs'
import useTypedSelector, { useAppDispatch } from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import fileDownload from 'Util/fileDownload'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import DownloadIcon from '@mui/icons-material/CloudDownload'
import { CircularProgress } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { c, t } from 'ttag'

export default function MobileOrderDetailPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/orders/mobile-orders`
  const previousLocation = usePreviousLocation(baseUrl)
  const dispatch = useAppDispatch()
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false)
  const [orderLineToBeRefunded, setOrderLineToBeRefunded] = useState<
    AffiliateTicketOrderLineShape | undefined
  >()

  const instanceIdParam = parseInt(useParams<{ id: string }>().id as string, 10)

  const { singleMobileOrder, loading, affiliateOrderReceiptLoading, refundLoading } =
    useTypedSelector(state => state.order)

  const { data: mobileOrderRefundOptions, isLoading: isRefundOptionsLoading } =
    useGetMobileOrderRefundOptionsQuery({ id: instanceIdParam })

  const {
    data: mobileOrderRefundList = [],
    isLoading: isRefundListLoading,
    refetch: refetchMobileOrderRefundList
  } = useGetMobileOrderRefundListQuery({ id: instanceIdParam })

  useEffect(() => {
    dispatch(
      fetchMobileOrder({
        id: instanceIdParam
      })
    )
  }, [dispatch, instanceIdParam])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  /**
   * Requests a refund for a mobile order or a specific order line.
   */
  const handleRequestRefund = (
    orderId: number,
    reason: string,
    note: string,
    refundAmount?: number
  ) => {
    const orderRefundData = {
      orderId,
      reason,
      ...(note && { note })
    }
    dispatch(
      orderLineToBeRefunded
        ? requestMobileOrderLineRefund({
            ...orderRefundData,
            orderLineId: orderLineToBeRefunded.id,
            refund_amount: refundAmount
          })
        : requestMobileOrderRefund(orderRefundData)
    )
      .then(unwrapResult)
      .then(() => {
        SnackbarUtils.success(t`Refund successfully requested.`)
        setIsRefundDialogOpen(false)

        // Re-fetch order and refund data to update the UI
        dispatch(fetchMobileOrder({ id: orderId }))
        refetchMobileOrderRefundList()
      })
      .catch((error: { error: string }) => {
        SnackbarUtils.error(error.error)
      })
  }

  const isRefundLoading = refundLoading === 'pending'

  function renderRefundContent(orderLine: AffiliateTicketOrderLineShape) {
    return (
      <TableCell>
        {orderLine.refund_summary.refund_available ? (
          <CustomChip
            onClick={() => handleRefundClick(orderLine)}
            label={
              isRefundLoading ? <CircularProgress sx={{ display: 'block' }} size={16} /> : t`Refund`
            }
          />
        ) : (
          <RefundInfo refundSummary={orderLine.refund_summary} />
        )}
      </TableCell>
    )
  }

  const handleRefundClick = (orderLine?: AffiliateTicketOrderLineShape) => {
    setOrderLineToBeRefunded(orderLine)
    setIsRefundDialogOpen(true)
  }

  const handleReceiptDownload = (id: number) => {
    const params = {
      id
    }
    dispatch(fetchMobileOrderReceipt(params))
      .then(unwrapResult)
      .then(response => {
        const fileNamePrefix = t`Waltti mobile order receipt`
        const fileName = `${fileNamePrefix}, ${singleMobileOrder?.external_id}`
        fileDownload(response, fileName)
      })
      .catch(error => {
        SnackbarUtils.error(error.error)
      })
  }

  const getRefundText = () => {
    if (singleMobileOrder?.refunded_by !== null) {
      return t`Order already refunded`
    }
    return t`Refund order`
  }

  const breadcrumbs = useSalesManagementBreadcrumbs([
    {
      title: t`Orders`
    },
    {
      title: t`Mobile orders`,
      link: baseUrl
    },
    {
      title: singleMobileOrder ? singleMobileOrder.external_id : ''
    }
  ])

  const isLoading = loading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <BackButton label={t`Back`} link={previousLocation} />
          {singleMobileOrder && (
            <Box>
              <CustomLoadingButton
                isLoading={affiliateOrderReceiptLoading === 'pending'}
                onClick={() => handleReceiptDownload(singleMobileOrder.id)}
                type="submit"
                icon={<DownloadIcon />}
                iconPosition="start"
                sx={{ mb: 1 }}
                size="medium"
              >{t`Download receipt`}</CustomLoadingButton>
              <CustomButton
                disabled={singleMobileOrder.refund_requested}
                title={getRefundText()}
                onClick={() => handleRefundClick()}
                sx={{ mb: 1, ml: 1 }}
              >
                {getRefundText()}
              </CustomButton>
              <MobileOrderRefundDialog
                orderLine={orderLineToBeRefunded}
                open={isRefundDialogOpen}
                mobileOrderRefundOptions={mobileOrderRefundOptions}
                isRefundOptionsLoading={isRefundOptionsLoading}
                handleClose={() => setIsRefundDialogOpen(false)}
                handleSubmit={(reason, note, refundAmount) =>
                  handleRequestRefund(singleMobileOrder.id, reason, note, refundAmount)
                }
              />
            </Box>
          )}
        </Box>
      </Box>
      <WithLoader loading={isLoading}>
        <Box>
          {singleMobileOrder && (
            <Box>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table aria-label={t`Order details table`}>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell colSpan={2}>{t`Order details`}</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{t`External ID`}</TableCell>
                      <TableCell>{singleMobileOrder.external_id}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Affiliate identity ID`}</TableCell>
                      <TableCell>
                        {showValueOrHyphen(singleMobileOrder.affiliate_identity_id)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Travel card number`}</TableCell>
                      <TableCell>
                        {' '}
                        {singleMobileOrder.travel_card_numbers.length > 0
                          ? singleMobileOrder.travel_card_numbers.toString()
                          : '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Waltti ID`}</TableCell>
                      <TableCell>{showValueOrHyphen(singleMobileOrder.waltti_user)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`User email`}</TableCell>
                      <TableCell>{showValueOrHyphen(singleMobileOrder.user_email)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Operator`}</TableCell>
                      <TableCell>{singleMobileOrder.operator_name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Sales channel`}</TableCell>
                      <TableCell>{singleMobileOrder.sales_channel_name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Client application version`}</TableCell>
                      <TableCell>{singleMobileOrder.client_application_version}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Created`}</TableCell>
                      <TableCell>{datetimeFormatter(singleMobileOrder.created)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Product scheme`}</TableCell>
                      <TableCell>{singleMobileOrder.product_scheme}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Receipt information`}</TableCell>
                      <TableCell>
                        {showValueOrHyphen(singleMobileOrder.receipt_information)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Payment method`}</TableCell>
                      <TableCell>
                        {showValueOrHyphen(singleMobileOrder.payment_method?.payment_method_name)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table aria-label={t`Ticket details table`}>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell colSpan={10}>{t`Tickets`}</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>{t`Order line ID`}</TableCell>
                      <TableCell>{t`Ticket ID`}</TableCell>
                      <TableCell>{t`Product ID`}</TableCell>
                      <TableCell>{t`Product name`}</TableCell>
                      <TableCell>{t`Customer group`}</TableCell>
                      <TableCell>{t`Municipality`}</TableCell>
                      <TableCell>{t`Start zone`}</TableCell>
                      <TableCell>{t`End zone`}</TableCell>
                      <TableCell>{c('Refund header').t`Refund`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {singleMobileOrder.ticket_order_lines.length > 0 ? (
                      singleMobileOrder.ticket_order_lines.map(line => (
                        <AffiliateOrderLineRow
                          key={line.ticket.ticket_id}
                          row={line}
                          renderRefundContent={() => renderRefundContent(line)}
                        />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell>{t`No order lines available`}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table aria-label={t`Payment info table`}>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell colSpan={5}>{t`Payment info`}</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>{t`Transactional ID`}</TableCell>
                      <TableCell>{t`Created`}</TableCell>
                      <TableCell>{t`Payment Operator`}</TableCell>
                      <TableCell>{t`Card PAN`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {singleMobileOrder.payment_method ? (
                      <AffiliatePaymentMethod payment_method={singleMobileOrder.payment_method} />
                    ) : (
                      <TableRow>
                        <TableCell>{t`No payment info available`}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <RefundHistoryList
                refundList={mobileOrderRefundList}
                ticketOrderLines={singleMobileOrder.ticket_order_lines}
                affiliateOrderRefundOptions={mobileOrderRefundOptions}
                isRefundOptionsLoading={isRefundOptionsLoading}
                isRefundListLoading={isRefundListLoading}
              />
            </Box>
          )}
        </Box>
      </WithLoader>
    </Box>
  )
}
